/**
 * Human-facing account name. Broker comes from GET /accounts (`broker`), never
 * assumed to be Schwab. Paper / TDA / tastytrade labels may already include a
 * "Broker: " prefix; strip it before adding the canonical one.
 *
 * Accepts `UiAccount`, strategy-page `Account`, and config-page `UnifiedAccount`.
 */

export type AccountNameFields = {
  paper?: boolean | null;
  broker?: string | null;
  /** Paper only: broker its market data comes from (first real account). */
  dataBroker?: string | null;
  accountNumber?: string | null;
  nickname?: string | null;
  label?: string | null;
  hashValue?: string | null;
  accountHash?: string | null;
};

/** Known casings; anything else is shown as the raw `broker` id. */
const BROKER_DISPLAY: Record<string, string> = {
  schwab: "Schwab",
  tastytrade: "Tastytrade",
  paper: "Paper",
  tda: "TDA",
  ibkr: "IBKR",
};

const BROKER_PREFIX_RE = /^(Paper|Schwab|tastytrade|Tastytrade|TDA|IBKR):\s*/i;

export function accountBroker(a: AccountNameFields): string {
  if (a.paper) return "paper";
  const explicit = (a.broker ?? "").trim().toLowerCase();
  if (explicit) return explicit;
  const hash = a.hashValue ?? a.accountHash ?? "";
  if (hash.startsWith("tastytrade:")) return "tastytrade";
  if (hash.startsWith("PAPER:")) return "paper";
  if (hash.startsWith("TDA:")) return "tda";
  return "schwab";
}

/** Broker whose live quotes this account reads. Paper borrows its owner's
 *  first real account (`dataBroker` from GET /accounts). */
export function quoteBroker(a: AccountNameFields): string {
  const broker = accountBroker(a);
  if (broker === "paper" && a.dataBroker) return a.dataBroker;
  return broker;
}

type PositionsAccount = AccountNameFields & { tokenId?: number | null };

/** Live broker positions URL, or null when this account has no live book.
 *  One route: hash picks paper / Schwab / tastytrade. */
export function livePositionsUrl(a: PositionsAccount): string | null {
  const hash = a.hashValue ?? a.accountHash ?? "";
  if (!hash) return null;
  const broker = accountBroker({ ...a, hashValue: hash });
  if (broker === "tda") return null;
  const tid = a.tokenId ?? -1;
  return `/accounts/${encodeURIComponent(hash)}/positions?tokenId=${tid}`;
}

export function brokerDisplayName(broker: string): string {
  return BROKER_DISPLAY[broker] ?? broker;
}

const BROKER_LOGOS: Record<string, string> = {
  schwab: "/schwab-logo.png",
  tastytrade: "/tastytrade-logo.png",
  paper: "/paper-trading.png",
  tda: "/tda-logo.png",
  ibkr: "/ibkr-logo.png",
};

export function brokerLogoSrc(broker: string): string | null {
  return BROKER_LOGOS[broker] ?? null;
}

/** Nickname for strategy-page headers. Falls back to account number. */
export function accountNickname(a: AccountNameFields): string {
  const nick = (a.nickname ?? "").trim();
  if (nick) return nick;
  if (accountBroker(a) === "paper") {
    return stripBrokerPrefix(a.label ?? "") || a.accountNumber || "Paper Trading";
  }
  return stripBrokerPrefix(a.accountNumber ?? a.label ?? "");
}

export function stripBrokerPrefix(label: string): string {
  return label.replace(BROKER_PREFIX_RE, "");
}

/** Sidebar / menu: "<Broker>: <account label>". */
export function prefixedAccountLabel(a: AccountNameFields): string {
  const name = brokerDisplayName(accountBroker(a));
  const base = stripBrokerPrefix(a.label ?? a.nickname ?? a.accountNumber ?? "");
  return `${name}: ${base}`;
}

export function strategyAccountName(a: AccountNameFields): string {
  const name = brokerDisplayName(accountBroker(a));
  if (accountBroker(a) === "paper") {
    return `${name}: ${a.nickname || stripBrokerPrefix(a.label ?? "") || a.accountNumber || "Paper Trading"}`;
  }
  const num = stripBrokerPrefix(a.accountNumber ?? a.label ?? "");
  return a.nickname ? `${name} #${num}: ${a.nickname}` : `${name} #${num}`;
}
